import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { StudentFragment } from '@school_ify/schoolify-repository/src/shared/types';

import TeacherClassesService from 'domain/services/teacher/classes';
import ChatService from 'domain/services/chat/ChatServices';

interface StudentFragmentExtended extends StudentFragment {
  active?: boolean;
}
interface DirectMessageInterface {
  id: string;
  user: StudentFragmentExtended;
  unread_messages: number;
}

interface DirectMessageState {
  error: string;
  loading: 'idle' | 'pending' | 'success' | 'failed';
  teacherClasses: any;
  classStudents: any;
  studentGrades: any;
  participants: DirectMessageInterface[];
}

const fetchUsers = createAsyncThunk('directMessages/fetchUsers', async () => {
  try {
    const users = await ChatService.getPossibleParticipants();
    const dataNormilized = users.map(e => {
      const obj = {
        id: e.id,
        user: e.participants[0]?.user,
        unread_messages: e.unread_messages,
      };
      return obj;
    });
    return dataNormilized;
  } catch (error) {
    throw new Error(error);
  }
});

export const fetchTeacherClasses = createAsyncThunk('grades/fetchTeacherClasses', async () => {
  try {
    const teacherClasses = await TeacherClassesService.getTeacherClasses();
    return teacherClasses;
  } catch (error) {
    throw new Error(error);
  }
});

export const fetchClassStudentsFromTeacher = createAsyncThunk(
  'grades/fetchClassStudentsFromTeacher',
  async (data: { class_id: string }) => {
    const { class_id } = data;
    try {
      const students = await TeacherClassesService.getClassStudentsFromTeacher({ class_id });
      return students;
    } catch (error) {
      throw new Error(error);
    }
  }
);

const initialState = {
  loading: 'idle',
  teacherClasses: [],
  classStudents: [],
  studentGrades: null,
  participants: [{ id: '', user: {}, unread_messages: 0 }],
} as DirectMessageState;

const gradesSlice = createSlice({
  name: 'gradesSlice',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchUsers.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.loading = 'success';
      state.participants = [...action.payload];
    });
    builder.addCase(fetchUsers.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = action.error.message;
    });

    builder.addCase(fetchTeacherClasses.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(fetchTeacherClasses.fulfilled, (state, action) => {
      state.loading = 'success';
      state.teacherClasses = [...action.payload];
    });
    builder.addCase(fetchTeacherClasses.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = action.error.message;
    });

    builder.addCase(fetchClassStudentsFromTeacher.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(fetchClassStudentsFromTeacher.fulfilled, (state, action) => {
      state.loading = 'success';
      state.classStudents = [...action.payload];
    });
    builder.addCase(fetchClassStudentsFromTeacher.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = action.error.message;
    });
  },
});

// eslint-disable-next-line no-empty-pattern
export const {} = gradesSlice.actions;
export default gradesSlice.reducer;
